import React, { useEffect } from "react";
import { useI18n } from "@/i18n";
import ContactForm from "@/components/ContactForm";
import { ArrowUpRight, Building2, LineChart, MapPin, ShieldCheck, Sparkles } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { langPath } from "@/lib/paths";

const IMG = {
  hero: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=1600&q=80",
  intro: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=80",
  how: "https://images.unsplash.com/photo-1567521464027-f127ff144326?auto=format&fit=crop&w=1400&q=80",
  aboutTop: "https://images.unsplash.com/photo-1503428593586-e225b39bddfe?auto=format&fit=crop&w=1400&q=80",
  project1: "https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=1200&q=80",
  project2: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1200&q=80",
  project3: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1200&q=80",
};

export default function Home() {
  const { t, language } = useI18n();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const attemptScroll = (retries = 5) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (retries > 0) {
          setTimeout(() => attemptScroll(retries - 1), 100);
        }
      };
      setTimeout(() => attemptScroll(), 100);
    }
  }, [location.hash, location.pathname]);

  const paragraphs = t("home.intro.paragraphs");
  const howParagraphs = t("home.how.paragraphs");
  const steps = t("home.how.steps");
  const projects = t("home.projects.items");
  const principles = t("home.principles.items");
  const faq = t("home.faq.items");
  const stats = t("home.stats.items");

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden isolate">
        <div className="absolute inset-0 z-0 bg-[color:var(--rv-ink)]">
          <img
            src={IMG.hero}
            alt={t("home.hero.imageAlt")}
            className="w-full h-full object-cover opacity-70"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--rv-ink)]/90 via-[color:var(--rv-ink)]/78 to-[color:var(--rv-ink)]/88" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-28 md:pt-32 md:pb-40 text-[color:var(--rv-bg)]">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.2em]">
              <Sparkles size={12} /> {t("home.hero.eyebrow")}
            </div>
            <h1 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
              {t("home.hero.title")}
            </h1>
            <p className="mt-6 text-lg md:text-xl opacity-90 max-w-2xl leading-relaxed">
              {t("home.hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}
                className="inline-flex items-center gap-2 rounded-full bg-[color:var(--rv-accent)] text-[color:var(--rv-ink)] px-6 py-3 text-sm font-semibold hover:bg-[color:var(--rv-accent-hover)]"
              >
                {t("home.hero.primaryCta")} <ArrowUpRight size={16} />
              </a>
              <a
                href="#how"
                onClick={(e) => { e.preventDefault(); document.getElementById("how")?.scrollIntoView({ behavior: "smooth" }); }}
                className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-6 py-3 text-sm font-medium hover:bg-white/10"
              >
                {t("home.hero.secondaryCta")}
              </a>
            </div>
            <p className="mt-6 text-xs uppercase tracking-widest opacity-70">{t("home.hero.note")}</p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-[color:var(--rv-line)] bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
          <h2 className="font-serif text-2xl md:text-3xl mb-8 max-w-2xl">{t("home.stats.title")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {Array.isArray(stats) && stats.map((s, i) => (
              <div key={i} className="p-5 rounded-xl bg-[color:var(--rv-panel)] border border-[color:var(--rv-line)]">
                <div className="font-serif text-4xl md:text-5xl text-[color:var(--rv-ink)]">{s.value}</div>
                <div className="mt-2 text-sm text-[color:var(--rv-muted)] leading-relaxed">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section id="about" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--rv-muted)] mb-3">{t("home.intro.eyebrow")}</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">{t("home.intro.title")}</h2>
              <div className="mt-8 aspect-[4/5] rounded-2xl overflow-hidden bg-[color:var(--rv-panel)]">
                <img src={IMG.intro} alt={t("home.intro.imageAlt")} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6">
            {Array.isArray(paragraphs) && paragraphs.map((p, i) => (
              <p key={i} className="text-lg leading-relaxed text-[color:var(--rv-ink)]/85">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-[color:var(--rv-panel)] py-20 md:py-28 border-y border-[color:var(--rv-line)]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--rv-muted)] mb-3">{t("home.how.eyebrow")}</div>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">{t("home.how.title")}</h2>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 space-y-4">
              {Array.isArray(steps) && steps.map((s, i) => (
                <div key={i} className="rounded-2xl border border-[color:var(--rv-line)] bg-white p-5 flex gap-4">
                  <div className="font-serif text-2xl text-[color:var(--rv-accent-strong)] w-10 shrink-0">{s.num}</div>
                  <div>
                    <div className="font-medium text-[color:var(--rv-ink)]">{s.title}</div>
                    <p className="text-sm text-[color:var(--rv-muted)] mt-1 leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img src={IMG.how} alt={t("home.how.imageAlt")} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img src={IMG.aboutTop} alt={t("home.intro.imageAlt")} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className="space-y-5">
                {Array.isArray(howParagraphs) && howParagraphs.map((p, i) => (
                  <p key={i} className="text-[15px] md:text-base leading-relaxed text-[color:var(--rv-ink)]/85">{p}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--rv-muted)] mb-3">{t("home.projects.eyebrow")}</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">{t("home.projects.title")}</h2>
              <p className="mt-4 text-[color:var(--rv-muted)]">{t("home.projects.subtitle")}</p>
            </div>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-1 text-sm text-[color:var(--rv-ink)] underline underline-offset-4"
            >
              {t("home.hero.primaryCta")} <ArrowUpRight size={14} />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {Array.isArray(projects) && projects.map((p, i) => (
              <article key={i} className="group rounded-2xl overflow-hidden bg-white border border-[color:var(--rv-line)]">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={[IMG.project1, IMG.project2, IMG.project3][i] || IMG.project1}
                    alt={p.imageAlt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--rv-accent-strong)]">{p.tag}</div>
                  <h3 className="mt-2 font-serif text-xl text-[color:var(--rv-ink)]">{p.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--rv-muted)] leading-relaxed">{p.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-[color:var(--rv-ink)] text-[color:var(--rv-bg)] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-70 mb-3">{t("home.principles.eyebrow")}</div>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight max-w-3xl">{t("home.principles.title")}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.isArray(principles) && principles.map((p, i) => (
              <div key={i} className="rounded-2xl border border-white/10 p-6 bg-white/5">
                <div className="h-9 w-9 rounded-lg bg-[color:var(--rv-accent)] text-[color:var(--rv-ink)] flex items-center justify-center">
                  {i === 0 ? <LineChart size={18} /> : i === 1 ? <ShieldCheck size={18} /> : <Building2 size={18} />}
                </div>
                <div className="mt-4 font-serif text-xl">{p.title}</div>
                <p className="mt-2 text-sm opacity-85 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--rv-muted)] mb-3">{t("home.faq.eyebrow")}</div>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">{t("home.faq.title")}</h2>
          <div className="mt-8 divide-y divide-[color:var(--rv-line)] rounded-2xl border border-[color:var(--rv-line)] overflow-hidden bg-white">
            {Array.isArray(faq) && faq.map((item, i) => (
              <details key={i} className="group p-5 open:bg-[color:var(--rv-panel)]">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4">
                  <span className="font-medium text-[color:var(--rv-ink)] leading-snug">{item.q}</span>
                  <span className="text-[color:var(--rv-muted)] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-[color:var(--rv-muted)] leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-[color:var(--rv-panel)] border-t border-[color:var(--rv-line)] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--rv-muted)] mb-3">{t("home.contact.eyebrow")}</div>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">{t("home.contact.title")}</h2>
            <p className="mt-4 text-[color:var(--rv-muted)] max-w-md">{t("home.contact.subtitle")}</p>

            <div className="mt-8 space-y-4 text-sm">
              <InfoItem icon={<MapPin size={16} />} label={t("home.contact.info.addressLabel")} value={t("home.contact.info.address")} />
              <InfoItem
                icon={<Sparkles size={16} />}
                label={t("home.contact.info.emailLabel")}
                value={<a href="mailto:contacto@ronaviraxa.com" className="underline underline-offset-4">{t("home.contact.info.email")}</a>}
              />
              <InfoItem icon={<LineChart size={16} />} label={t("home.contact.info.hoursLabel")} value={t("home.contact.info.hours")} />
            </div>

            <div className="mt-8 text-xs text-[color:var(--rv-muted)]">
              <Link to={langPath(language, "/privacy")} className="underline underline-offset-4">{t("nav.privacy")}</Link>
              <span className="mx-2">·</span>
              <Link to={langPath(language, "/cookies")} className="underline underline-offset-4">{t("nav.cookies")}</Link>
              <span className="mx-2">·</span>
              <Link to={langPath(language, "/terms")} className="underline underline-offset-4">{t("nav.terms")}</Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-white border border-[color:var(--rv-line)] p-6 md:p-8">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 h-8 w-8 rounded-full bg-white border border-[color:var(--rv-line)] flex items-center justify-center text-[color:var(--rv-ink)]">{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--rv-muted)]">{label}</div>
        <div className="text-[color:var(--rv-ink)]">{value}</div>
      </div>
    </div>
  );
}
